import React from 'react';
import { AnimatedSkeletonLoader } from '@usepepper/ui/atoms/animated-skeleton-loader';
import { BORDER_RADIUS_SMALL } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import { Divider } from '@usepepper/ui/atoms/divider';
import { OrderSummaryMetricRow } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/hooks/use-order-summary-metric-rows';
import { OrderSummaryMetrics } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/order-summary-metrics';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const SKELETON_ROW_COUNT = 4;
const SKELETON_ROW_FADE_STEP = 0.2;
const SKELETON_BORDER_RADIUS = 20;
const SKELETON_HEIGHT = 14;
const SKELETON_LABEL_WIDTH = 80;
const SKELETON_VALUE_WIDTH = 32;

interface OrderSummaryCardProps {
  additionalDetailsElement?: React.ReactNode;
  formattedDate: string;
  formattedTimeslot?: string;
  icon: React.ReactNode;
  isLoading?: boolean;
  metricRows: OrderSummaryMetricRow[];
  orderActionElement?: React.JSX.Element | null;
  selectedFulfillmentDisplay: string;
  subtotalDisplayString: string;
  unitCount: number;
}

export const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({
  additionalDetailsElement,
  formattedDate,
  formattedTimeslot,
  icon,
  isLoading = false,
  metricRows,
  orderActionElement,
  selectedFulfillmentDisplay,
  subtotalDisplayString,
  unitCount,
}) => {
  const [styles, theme] = useStyles();

  return (
    <View style={styles.orderSummaryCardContainer}>
      <View style={styles.deliveryIconContainer}>{icon}</View>
      <View style={styles.orderSummaryCardContentContainer}>
        <View style={styles.fulfillmentContainer}>
          <View style={styles.fulfillmentTypeAndDate}>
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base"
            >
              {selectedFulfillmentDisplay}
            </Typography>
            <View style={styles.fulfillmentDateTimeslot}>
              <Typography
                color={theme.sk.color.text.primary}
                numberOfLines={1}
                variant="paragraph-base-600"
              >
                {[formattedDate, formattedTimeslot].filter(Boolean).join(' ')}
              </Typography>
            </View>
          </View>
          {orderActionElement}
        </View>
        <Divider />
        {isLoading ? (
          <View style={styles.loadingContainer}>
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <View
                key={index}
                style={{
                  ...styles.loadingRow,
                  opacity: 1 - index * SKELETON_ROW_FADE_STEP,
                }}
              >
                <AnimatedSkeletonLoader
                  borderRadius={SKELETON_BORDER_RADIUS}
                  height={SKELETON_HEIGHT}
                  width={SKELETON_LABEL_WIDTH}
                />
                <AnimatedSkeletonLoader
                  borderRadius={SKELETON_BORDER_RADIUS}
                  height={SKELETON_HEIGHT}
                  width={SKELETON_VALUE_WIDTH}
                />
              </View>
            ))}
          </View>
        ) : (
          <OrderSummaryMetrics
            metricRows={metricRows}
            subtotalDisplayString={subtotalDisplayString}
            unitCount={unitCount}
          />
        )}
        {additionalDetailsElement ? (
          <>
            <Divider />
            {additionalDetailsElement}
          </>
        ) : null}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  deliveryIconContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: theme.sk.radii.radiusRound,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  fulfillmentContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  fulfillmentDateTimeslot: {
    flex: 1,
    flexShrink: 1,
  },
  fulfillmentTypeAndDate: {
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  loadingContainer: {
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceSmall,
    paddingTop: theme.sk.spacing.spaceSmall,
  },
  loadingRow: {
    flexDirection: 'row',
    height: 28,
    justifyContent: 'space-between',
    paddingBottom: theme.sk.spacing.spaceSmall,
    paddingTop: theme.sk.spacing.spaceSmall,
  },
  orderSummaryCardContainer: {
    borderColor: theme.colors.divider,
    borderRadius: BORDER_RADIUS_SMALL,
    borderWidth: 1,
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceSMedium,
    padding: theme.sk.spacing.spaceMedium,
  },
  orderSummaryCardContentContainer: {
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
